import React, { useEffect, useRef, useState } from 'react';

import { importLibrary, setOptions } from '@googlemaps/js-api-loader';

import type {
  RedZoneObservation,
  RedZoneObservationClassification,
  RedZoneObservationEntityType,
} from '../../../../api/redZone/post';

import styles from './styles/styles.module.css';

let googleMapsConfigured = false;

export type RedZoneDraftLocation = {
  coordinates: [number, number]; // [longitude, latitude]
  formattedAddress?: string;
  placeId?: string;
  source: 'MAP_DROP' | 'ADDRESS_SEARCH';
};

interface RedZoneObservationMapProps {
  apiKey?: string;
  siteAddress?: string;
  siteName?: string;
  observations: RedZoneObservation[];
  observationsLoading?: boolean;
  draftLocation: RedZoneDraftLocation | null;
  dropMode: RedZoneObservationEntityType | null;
  selectedObservationId?: string | null;
  onMapDrop: (location: RedZoneDraftLocation) => void;
  onObservationSelect: (observation: RedZoneObservation) => void;
}

const DEFAULT_CENTER = {
  lat: 39.8283,
  lng: -98.5795,
};

const ENTITY_MARKER_LABEL: Record<RedZoneObservationEntityType, string> = {
  VEHICLE: 'V',
  PERSON: 'P',
  ACTIVITY: 'A',
  HAZARD: 'H',
  OTHER: 'O',
};

const CLASSIFICATION_CLASS: Record<RedZoneObservationClassification, string> = {
  BASELINE: styles.observationMarkerBaseline,
  UNUSUAL: styles.observationMarkerUnusual,
  SUSPICIOUS: styles.observationMarkerSuspicious,
  THREAT: styles.observationMarkerThreat,
};

function createObservationMarkerContent(
  observation: RedZoneObservation,
  selected: boolean
) {
  /*
   * The outer wrapper is what we resize as the map zoom changes.
   *
   * The inner marker keeps the existing CSS hover/selected transforms,
   * so zoom scaling does not fight with those styles.
   */
  const wrapper = document.createElement('div');
  wrapper.style.transformOrigin = 'center bottom';
  wrapper.style.transition = 'transform 120ms ease-out';
  wrapper.style.willChange = 'transform';

  const marker = document.createElement('div');

  marker.className = [
    styles.observationMarker,
    CLASSIFICATION_CLASS[observation.classification],
    selected ? styles.observationMarkerSelected : '',
  ]
    .filter(Boolean)
    .join(' ');

  const entity = document.createElement('span');
  entity.className = styles.observationMarkerEntity;
  entity.textContent = ENTITY_MARKER_LABEL[observation.entityType];
  marker.appendChild(entity);

  const classification = document.createElement('span');
  classification.className = styles.observationMarkerClassification;
  classification.textContent = observation.classification.charAt(0);
  marker.appendChild(classification);

  wrapper.appendChild(marker);

  return wrapper;
}

function getObservationMarkerScale(zoom: number) {
  /*
   * Observation markers are full size while working close to the
   * property, then progressively shrink as the map zooms out.
   *
   * Zoom 18+: 100%
   * Zoom 14:  ~71%
   * Zoom 10:  ~42%
   * Zoom 8-:   28%
   */
  const minimumZoom = 8;
  const maximumZoom = 18;
  const minimumScale = 0.28;
  const maximumScale = 1;

  if (zoom <= minimumZoom) {
    return minimumScale;
  }

  if (zoom >= maximumZoom) {
    return maximumScale;
  }

  const progress = (zoom - minimumZoom) / (maximumZoom - minimumZoom);

  return minimumScale + progress * (maximumScale - minimumScale);
}

function createDraftMarkerContent(
  entityType: RedZoneObservationEntityType | null
) {
  const marker = document.createElement('div');
  marker.className = `${styles.observationMarker} ${styles.draftMarker}`;

  const entity = document.createElement('span');
  entity.className = styles.observationMarkerEntity;
  entity.textContent = entityType ? ENTITY_MARKER_LABEL[entityType] : '+';

  marker.appendChild(entity);
  return marker;
}

function createSiteMarkerContent(siteName?: string) {
  const wrapper = document.createElement('div');

  wrapper.style.display = 'flex';
  wrapper.style.flexDirection = 'column';
  wrapper.style.alignItems = 'center';
  wrapper.style.pointerEvents = 'none';

  const label = document.createElement('div');
  label.textContent = siteName?.trim() || 'Protected Site';
  label.style.backgroundColor = '#ffffff';
  label.style.color = '#202124';
  label.style.fontSize = '12px';
  label.style.fontWeight = '700';
  label.style.lineHeight = '1.2';
  label.style.padding = '6px 9px';
  label.style.border = '1px solid #d9dde3';
  label.style.borderRadius = '8px';
  label.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.16)';
  label.style.whiteSpace = 'nowrap';
  label.style.marginBottom = '6px';

  const pin = document.createElement('div');
  pin.style.position = 'relative';
  pin.style.width = '26px';
  pin.style.height = '26px';
  pin.style.backgroundColor = '#1a73e8';
  pin.style.border = '3px solid #ffffff';
  pin.style.borderRadius = '50%';
  pin.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.28)';

  const dot = document.createElement('div');
  dot.style.position = 'absolute';
  dot.style.width = '8px';
  dot.style.height = '8px';
  dot.style.backgroundColor = '#ffffff';
  dot.style.borderRadius = '50%';
  dot.style.top = '6px';
  dot.style.left = '6px';

  pin.appendChild(dot);
  wrapper.appendChild(label);
  wrapper.appendChild(pin);

  return wrapper;
}

const RedZoneObservationMap: React.FC<RedZoneObservationMapProps> = ({
  apiKey,
  siteAddress,
  siteName,
  observations,
  observationsLoading = false,
  draftLocation,
  dropMode,
  selectedObservationId,
  onMapDrop,
  onObservationSelect,
}) => {
  const mapElementRef = useRef<HTMLDivElement | null>(null);
  const placeAutocompleteHostRef = useRef<HTMLDivElement | null>(null);

  const mapRef = useRef<any>(null);
  const geocoderRef = useRef<any>(null);
  const advancedMarkerClassRef = useRef<any>(null);
  const mapClickListenerRef = useRef<any>(null);
  const zoomListenerRef = useRef<any>(null);
  const observationMarkersRef = useRef<any[]>([]);
  const draftMarkerRef = useRef<any>(null);
  const searchMarkerRef = useRef<any>(null);
  const siteMarkerRef = useRef<any>(null);

  const dropModeRef = useRef(dropMode);
  const onMapDropRef = useRef(onMapDrop);
  const onObservationSelectRef = useRef(onObservationSelect);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [placeSearchError, setPlaceSearchError] = useState('');
  const [mapReadyVersion, setMapReadyVersion] = useState(0);

  useEffect(() => {
    dropModeRef.current = dropMode;
  }, [dropMode]);

  useEffect(() => {
    onMapDropRef.current = onMapDrop;
  }, [onMapDrop]);

  useEffect(() => {
    onObservationSelectRef.current = onObservationSelect;
  }, [onObservationSelect]);

  useEffect(() => {
    let cancelled = false;
    let placeAutocomplete: any = null;
    let placeSelectHandler: ((event: any) => void) | null = null;

    async function initializeMap() {
      if (!apiKey) {
        setLoading(false);
        setError('Google Maps API key is not configured.');
        return;
      }

      if (!mapElementRef.current) {
        return;
      }

      try {
        setLoading(true);
        setError('');

        if (!googleMapsConfigured) {
          setOptions({
            key: apiKey,
            v: 'weekly',
          });

          googleMapsConfigured = true;
        }

        const [mapsLibrary, geocodingLibrary, markerLibrary, placesLibrary] =
          await Promise.all([
            importLibrary('maps'),
            importLibrary('geocoding'),
            importLibrary('marker'),
            importLibrary('places'),
          ]);

        if (cancelled) {
          return;
        }

        const { Map } = mapsLibrary as any;
        const { Geocoder } = geocodingLibrary as any;
        const { AdvancedMarkerElement } = markerLibrary as any;
        const { PlaceAutocompleteElement } = placesLibrary as any;

        const map = new Map(mapElementRef.current, {
          center: DEFAULT_CENTER,
          zoom: 4,
          mapId: process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID',
          mapTypeId: 'roadmap',
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          clickableIcons: false,
          gestureHandling: 'greedy',
        });

        mapRef.current = map;
        geocoderRef.current = new Geocoder();
        advancedMarkerClassRef.current = AdvancedMarkerElement;

        /*
         * Keep historical observation markers visually small as the
         * operator zooms out so many observations can remain readable
         * across a larger geographic area.
         */
        zoomListenerRef.current = map.addListener('zoom_changed', () => {
          const zoom = Number(map.getZoom() ?? 18);
          const scale = getObservationMarkerScale(zoom);

          for (const marker of observationMarkersRef.current) {
            const scaleElement = marker.__redZoneScaleElement;

            if (scaleElement) {
              scaleElement.style.transform = `scale(${scale})`;
            }
          }
        });

        mapClickListenerRef.current = map.addListener(
          'click',
          async (event: any) => {
            const activeDropMode = dropModeRef.current;

            if (!activeDropMode || !event?.latLng) {
              return;
            }

            const latitude = event.latLng.lat();
            const longitude = event.latLng.lng();

            let formattedAddress: string | undefined;
            let placeId: string | undefined;

            try {
              const result = await geocoderRef.current?.geocode({
                location: {
                  lat: latitude,
                  lng: longitude,
                },
              });

              const first = result?.results?.[0];
              formattedAddress = first?.formatted_address;
              placeId = first?.place_id;
            } catch (geocodeError) {
              console.warn('Red Zone reverse geocoding failed:', geocodeError);
            }

            onMapDropRef.current({
              coordinates: [longitude, latitude],
              formattedAddress,
              placeId,
              source: 'MAP_DROP',
            });
          }
        );

        if (siteAddress?.trim()) {
          try {
            const result = await geocoderRef.current.geocode({
              address: siteAddress.trim(),
            });

            const first = result?.results?.[0];
            const location = first?.geometry?.location;

            if (location) {
              map.setCenter(location);
              map.setZoom(18);

              if (siteMarkerRef.current) {
                siteMarkerRef.current.map = null;
              }

              siteMarkerRef.current = new AdvancedMarkerElement({
                map,
                position: location,
                content: createSiteMarkerContent(siteName),
                title:
                  siteName?.trim() ||
                  first?.formatted_address ||
                  siteAddress.trim(),
                zIndex: 1000,
              });
            } else {
              console.warn(
                'Google Maps returned no location for the selected site address.'
              );
            }
          } catch (geocodeError) {
            console.warn(
              'Could not center Red Zone map on site:',
              geocodeError
            );
          }
        }

        if (placeAutocompleteHostRef.current) {
          placeAutocomplete = new PlaceAutocompleteElement({
            placeholder: 'Search for an address or place',
          });

          /*
           * Force Google's web component into a light theme.
           *
           * The component can inherit the browser/app color-scheme,
           * which is why it was rendering black even though the rest
           * of the Red Zone page is light.
           */
          placeAutocomplete.style.setProperty('width', '100%');
          placeAutocomplete.style.setProperty('display', 'block');
          placeAutocomplete.style.setProperty('color-scheme', 'light');
          placeAutocomplete.style.setProperty('background-color', '#ffffff');
          placeAutocomplete.style.setProperty('color', '#4f545a');
          placeAutocomplete.style.setProperty('border', '1px solid #dfe2e7');
          placeAutocomplete.style.setProperty('border-radius', '9px');
          placeAutocomplete.style.setProperty('font-size', '11px');

          placeAutocompleteHostRef.current.style.setProperty(
            'color-scheme',
            'light'
          );
          placeAutocompleteHostRef.current.style.setProperty(
            'background-color',
            '#ffffff'
          );

          placeAutocompleteHostRef.current.replaceChildren(placeAutocomplete);

          placeSelectHandler = async (event: any) => {
            try {
              setPlaceSearchError('');

              const prediction = event?.placePrediction;
              if (!prediction) return;

              const place = prediction.toPlace();

              await place.fetchFields({
                fields: [
                  'displayName',
                  'formattedAddress',
                  'location',
                  'viewport',
                  'id',
                ],
              });

              if (place.viewport) {
                map.fitBounds(place.viewport);
              } else if (place.location) {
                map.setCenter(place.location);
                map.setZoom(17);
              }

              if (searchMarkerRef.current) {
                searchMarkerRef.current.map = null;
              }

              if (place.location) {
                const content = document.createElement('div');
                content.className = styles.searchMarker;
                content.textContent = '•';

                searchMarkerRef.current = new AdvancedMarkerElement({
                  map,
                  position: place.location,
                  content,
                  title:
                    place.formattedAddress ||
                    place.displayName ||
                    'Search result',
                });
              }
            } catch (placeError) {
              console.error('Place search failed:', placeError);
              setPlaceSearchError(
                'Could not open that place. Please try another search.'
              );
            }
          };

          placeAutocomplete.addEventListener('gmp-select', placeSelectHandler);
        }

        setMapReadyVersion((value) => value + 1);
        setLoading(false);
      } catch (mapError) {
        console.error('Red Zone map initialization failed:', mapError);

        if (!cancelled) {
          setLoading(false);
          setError('Unable to load the Red Zone map.');
        }
      }
    }

    void initializeMap();

    return () => {
      cancelled = true;

      mapClickListenerRef.current?.remove?.();
      mapClickListenerRef.current = null;

      zoomListenerRef.current?.remove?.();
      zoomListenerRef.current = null;

      for (const marker of observationMarkersRef.current) {
        marker.map = null;
      }
      observationMarkersRef.current = [];

      if (draftMarkerRef.current) {
        draftMarkerRef.current.map = null;
        draftMarkerRef.current = null;
      }

      if (searchMarkerRef.current) {
        searchMarkerRef.current.map = null;
        searchMarkerRef.current = null;
      }

      if (siteMarkerRef.current) {
        siteMarkerRef.current.map = null;
        siteMarkerRef.current = null;
      }

      if (placeAutocomplete && placeSelectHandler) {
        placeAutocomplete.removeEventListener('gmp-select', placeSelectHandler);
      }

      mapRef.current = null;
    };
  }, [apiKey, siteAddress, siteName]);

  useEffect(() => {
    const map = mapRef.current;
    const AdvancedMarkerElement = advancedMarkerClassRef.current;

    if (!map || !AdvancedMarkerElement) {
      return;
    }

    for (const marker of observationMarkersRef.current) {
      marker.map = null;
    }
    observationMarkersRef.current = [];

    for (const observation of observations) {
      const coordinates = observation.location?.coordinates;

      if (!Array.isArray(coordinates) || coordinates.length !== 2) {
        continue;
      }

      const [longitude, latitude] = coordinates;

      if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
        continue;
      }

      const markerContent = createObservationMarkerContent(
        observation,
        observation._id === selectedObservationId
      );

      const currentZoom = Number(map.getZoom() ?? 18);

      markerContent.style.transform = `scale(${getObservationMarkerScale(currentZoom)})`;

      const marker = new AdvancedMarkerElement({
        map,
        position: {
          lat: latitude,
          lng: longitude,
        },
        content: markerContent,
        title:
          observation.location.formattedAddress ||
          `${observation.entityType} • ${observation.classification}`,
        gmpClickable: true,
      });

      marker.__redZoneScaleElement = markerContent;

      const listener = marker.addListener('click', () => {
        onObservationSelectRef.current(observation);
      });

      marker.__redZoneListener = listener;
      observationMarkersRef.current.push(marker);
    }

    return () => {
      for (const marker of observationMarkersRef.current) {
        marker.__redZoneListener?.remove?.();
        marker.map = null;
      }
      observationMarkersRef.current = [];
    };
  }, [observations, selectedObservationId, mapReadyVersion]);

  useEffect(() => {
    const map = mapRef.current;
    const AdvancedMarkerElement = advancedMarkerClassRef.current;

    if (draftMarkerRef.current) {
      draftMarkerRef.current.map = null;
      draftMarkerRef.current = null;
    }

    if (!map || !AdvancedMarkerElement || !draftLocation) {
      return;
    }

    const [longitude, latitude] = draftLocation.coordinates;

    draftMarkerRef.current = new AdvancedMarkerElement({
      map,
      position: {
        lat: latitude,
        lng: longitude,
      },
      content: createDraftMarkerContent(dropMode),
      title: 'Unsaved Red Zone observation',
    });

    map.panTo({
      lat: latitude,
      lng: longitude,
    });
  }, [draftLocation, dropMode, mapReadyVersion]);

  if (!apiKey) {
    return (
      <div className={styles.redZoneMapUnavailable}>
        <strong className={styles.redZoneMapUnavailableTitle}>
          Map unavailable
        </strong>
        <span>REACT_APP_GOOGLE_MAPS_API_KEY is not configured.</span>
      </div>
    );
  }

  return (
    <div className={styles.redZoneMapWrapper}>
      <div className={styles.redZoneMapSearchRow}>
        <div className={styles.redZoneMapSearchLabel}>Find a location</div>

        <div
          ref={placeAutocompleteHostRef}
          className={styles.redZonePlaceAutocompleteHost}
        />
      </div>

      {placeSearchError && (
        <div className={styles.redZonePlaceSearchError}>{placeSearchError}</div>
      )}

      <div className={styles.redZoneMapCanvasWrap}>
        <div ref={mapElementRef} className={styles.redZoneMapCanvas} />

        {loading && (
          <div className={styles.redZoneMapLoadingOverlay}>Loading map…</div>
        )}

        {!loading && observationsLoading && (
          <div
            className={styles.redZoneObservationLoadingNotice}
            role="status"
            aria-live="polite"
          >
            <span
              className={styles.redZoneObservationLoadingPulse}
              aria-hidden="true"
            />

            <div className={styles.redZoneObservationLoadingCopy}>
              <strong>Loading observation history…</strong>
              <span>Retrieving map markers for the selected site.</span>
            </div>
          </div>
        )}
      </div>

      {error && <div className={styles.redZoneMapError}>{error}</div>}

      <div className={styles.redZoneMapStatus}>
        <span>
          {dropMode
            ? `Click the map to place a ${dropMode.toLowerCase()} observation.`
            : 'Choose an observation type above the map to start a drop.'}
        </span>

        <div className={styles.mapLegend}>
          <span className={styles.legendItem}>
            <i className={`${styles.legendDot} ${styles.legendBaseline}`} />
            Baseline
          </span>
          <span className={styles.legendItem}>
            <i className={`${styles.legendDot} ${styles.legendUnusual}`} />
            Unusual
          </span>
          <span className={styles.legendItem}>
            <i className={`${styles.legendDot} ${styles.legendSuspicious}`} />
            Suspicious
          </span>
          <span className={styles.legendItem}>
            <i className={`${styles.legendDot} ${styles.legendThreat}`} />
            Threat
          </span>
        </div>
      </div>
    </div>
  );
};

export default RedZoneObservationMap;
